import { supabaseChildrenService } from './supabase/children.service'
import type { Child } from '@/models/Child'
import type { ChildRow } from '@/models/supabase/Child'
import { shopService } from './shop.service'
import { useAnalytics, type ActivationContext } from '@/composables/useAnalytics'

const analytics = useAnalytics()

/** Âge en années révolues à partir d'une date de naissance ISO. */
function ageFromBirthDate(birthDate?: string | null): number | undefined {
    if (!birthDate) return undefined
    // Ancrage à midi local : évite qu'un décalage de fuseau fasse basculer d'un an.
    const born = new Date(birthDate.length === 10 ? `${birthDate}T12:00:00` : birthDate)
    if (Number.isNaN(born.getTime())) return undefined
    const today = new Date()
    let age = today.getFullYear() - born.getFullYear()
    const monthDiff = today.getMonth() - born.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < born.getDate())) age--
    return age >= 0 ? age : undefined
}

export const childService = {
    /**
     * Create a new child
     */
    async createChild(data: {
        name: string,
        household_id: string,
        birth_date?: string | null,
        gender?: string | null,
        avatar?: string | null,
        color?: string
    }, ctx?: ActivationContext): Promise<Child> {
        try {
            const child = await supabaseChildrenService.createChild({
                name: data.name,
                household_id: data.household_id,
                birth_date: data.birth_date === '' ? null : (data.birth_date || null),
                gender: data.gender || null,
                avatar: data.avatar || null,
                color: data.color
            })

            // Grant free starter items (avatar, pose, background) from shop
            try {
                const starterItems = await shopService.grantFreeStarterItems(child.id)
                console.log(`✅ Free starter items granted to ${child.name}:`, starterItems)
            } catch (avatarError) {
                console.error(`⚠️ Failed to grant starter items to ${child.name}:`, avatarError)
                // Ne pas bloquer la création de l'enfant si l'assignation d'avatar échoue
            }

            // Premier jalon du parcours d'activation. Émis ici et non depuis le
            // store enfants : l'interface appelle ce service en direct, le store
            // n'est traversé par aucun écran de création.
            analytics.logChildCreated(child.id, ageFromBirthDate(data.birth_date), data.gender || undefined, ctx)

            // Convertir le format Supabase en format compatible avec l'application
            return this.mapChildRowToChild(child)
        } catch (error) {
            console.error('Error in childService.createChild:', error)
            throw error
        }
    },

    /**
     * Get child by ID
     */
    async getChild(childId: string): Promise<Child | null> {
        // console.log('👶 [ChildService] getChild called for childId:', childId);
        try {
            const child = await supabaseChildrenService.getChild(childId);
            // console.log('✅ [ChildService] getChild result:', child ? 'found' : 'not found');
            return child ? this.mapChildRowToChild(child) : null;
        } catch (error) {
            console.error('❌ [ChildService] Error in getChild:', error);
            throw error
        }
    },

    /**
     * Delete a child by ID
     */
    async deleteChild(childId: string): Promise<void> {
        // console.log('🗑️ [ChildService] deleteChild called for childId:', childId);
        try {
            await supabaseChildrenService.deleteChild(childId);
            // Signal de désengagement : émis ici et non depuis le store enfants,
            // que les écrans de suppression ne traversent pas.
            analytics.logChildDeleted(childId);
        } catch (error) {
            console.error(`❌ [ChildService] Error in deleteChild for child ${childId}:`, error)
            throw error
        }
    },

    /**
     * Get child by ID (alias for getChild)
     */
    async getChildById(childId: string): Promise<Child | null> {
        // console.log('👶 [ChildService] getChildById called for childId:', childId);
        return this.getChild(childId)
    },

    /**
     * Get children for a household
     */
    async getHouseholdChildren(householdId: string): Promise<Child[]> {
        // console.log('👶 [ChildService] getHouseholdChildren called for householdId:', householdId);
        try {
            const children = await supabaseChildrenService.getHouseholdChildren(householdId);
            // console.log('✅ [ChildService] getHouseholdChildren result:', children.length, 'children');
            return children.map(child => this.mapChildRowToChild(child))
        } catch (error) {
            console.error('❌ [ChildService] Error in getHouseholdChildren:', error)
            throw error
        }
    },

    /**
     * Get children by household ID (alias for getHouseholdChildren)
     */
    async getChildrenByHouseholdId(householdId: string): Promise<Child[]> {
        // console.log('👶 [ChildService] getChildrenByHouseholdId called for householdId:', householdId);
        return this.getHouseholdChildren(householdId)
    },

    /**
     * Update a child
     */
    async updateChild(childId: string, updates: Partial<Child>): Promise<Child> {
        // console.log('✏️ [ChildService] updateChild called:', {
        //     childId: childId,
        //         updates: updates
        // });

        // Nettoyer les données avant l'envoi
        const cleanUpdates = {
            ...updates,
            // Convertir les chaînes vides en null pour les dates
            birth_date: updates.birth_date === '' ? null : updates.birth_date
        };

        const childRow = await supabaseChildrenService.updateChild(childId, cleanUpdates as any);
        // console.log('✅ [ChildService] updateChild completed');
        return this.mapChildRowToChild(childRow);
    },

    /**
     * Update child points
     */
    async updateChildPoints(childId: string, pointsToAdd: number): Promise<void> {
        try {
            // Get current child data
            const child = await this.getChild(childId);
            const newPoints = Math.max(0, (child.points || 0) + pointsToAdd);

            // Update points in database
            await this.updateChild(childId, { points: newPoints });
        } catch (error) {
            console.error('Error updating child points:', error);
            throw error;
        }
    },

    /**
     * Map a Supabase child row to our app's Child model
     */
    mapChildRowToChild(childRow: ChildRow): Child {
        // Utiliser une approche plus sûre pour accéder aux propriétés
        const row = childRow as any; // Conversion temporaire pour accéder aux propriétés dynamiquement

        return {
            id: row.id,
            household_id: row.household_id,
            user_id: '', // Valeur par défaut
            name: row.name || '',
            birth_date: row.birth_date || '',
            gender: row.gender || undefined,
            avatar: row.avatar || '',
            color: row.color || '',
            points: row.points || 0,
            created_by: '', // Valeur par défaut
            created_at: row.created_at ? new Date(row.created_at).getTime() : Date.now()
        };
    }
}

